import { useEffect, useMemo, useRef, useState } from "react"
import { useFormikContext } from "formik"
import { Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { DeliveryTimesField, DynamicConfigKey, MeetingServiceTabValues } from "./form/formTypes"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { calculateTotalAmountFromDeliveries } from "./drawer/orderSummary/helpers"
import { StandardLoadingButton } from "../../buttons/LoadingButton"
import { useMoney } from "../../../../modules/currency/useMoney"
import { SelectedPriceListItem } from "../createOrderTypes"

const SUBMIT_LOADING_TIMEOUT_MS = 2000

const useStyles = makeStyles()((_theme) => ({
    total: {
        display: "flex",
        alignItems: "end",
        flexWrap: "nowrap",
        gap: ".5rem",
    },
    submitButton: {
        justifyContent: "space-between",
        flexWrap: "nowrap",
        alignItems: "flex-start",
    },
}))
type ConfirmOrderSubmitButtonProps = Readonly<{
    isRefundOrder: boolean
}>

export function ConfirmOrderSubmitButton({ isRefundOrder }: ConfirmOrderSubmitButtonProps) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})

    const { values, isSubmitting, submitCount } = useFormikContext<MeetingServiceTabValues>()

    const [loading, setLoading] = useState<boolean>(false)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }

        if (isSubmitting) {
            setLoading(true)
            timerRef.current = setTimeout(() => {
                setLoading(false)
                timerRef.current = null
            }, SUBMIT_LOADING_TIMEOUT_MS)
        } else {
            setLoading(false)
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
        }
    }, [isSubmitting, submitCount])

    const deliveries: DeliveryTimesField | undefined = values[DynamicConfigKey.MULTIPLE_DELIVERIES]
    const { priceListIncludeVat }: SelectedPriceListItem = values["priceList"]

    const totalAmount = useMemo(
        () => deliveries && calculateTotalAmountFromDeliveries(deliveries.deliveryTimes),
        [deliveries?.deliveryTimes],
    )

    return (
        <StandardLoadingButton loading={loading} type="submit" className={classes.submitButton}>
            <LocalizedStrict id="create-order-confirm-order-button">
                <Typography variant="inherit">Next</Typography>
            </LocalizedStrict>
            {totalAmount && (
                <Typography variant="caption" textAlign="right" className={classes.total}>
                    <LocalizedStrict id={priceListIncludeVat ? "create-order-vat-included" : "create-order-vat-excl"}>
                        <Typography variant="caption">{priceListIncludeVat ? "(VAT incl.)" : "(VAT excl.)"}</Typography>
                    </LocalizedStrict>
                    <Typography>
                        {isRefundOrder && "-"}
                        {moneyFactory.format(totalAmount)}
                    </Typography>
                </Typography>
            )}
        </StandardLoadingButton>
    )
}
